"use client";

import { VscAccount } from "react-icons/vsc";
import {
  Avatar,
  Box,
  Icon,
  IconButton,
  Modal,
  Paper,
  Stack,
} from "@mui/material";
import React, { useRef } from "react";
import {
  Tree,
  MultiBackend,
  getBackendOptions,
  NodeModel,
  DndProvider,
} from "@minoru/react-dnd-treeview";
import { CustomDrawerNode } from "./CustomDrawerNode.View";
import styles from "./Tree.module.css";
import { usePathname, useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useTheme } from "@cbex/utils/theme";
import { CustomTypographyComponent } from "@cbex/ui/text";
import { AppContext } from "@cbex/utils/context";

interface DrawerProps {
  treeData: any[];
}

export const Drawer = ({ treeData }: DrawerProps) => {
  const useIsomorphicLayoutEffect =
    typeof window !== "undefined" ? React.useLayoutEffect : React.useEffect;
  const [localTreeData, setLocalTreeData] = React.useState<NodeModel[]>();
  const [selectedNode, setSelectedNode] = React.useState<NodeModel>();
  const t = useTranslations();
  const [initialOpen, setInitialOpen] = React.useState<number[]>([]);
  const [currentID, setCurrentID] = React.useState<number | string>(0);
  const treeRef = useRef(null);
  const pathName = usePathname();
  const router = useRouter();
  const theme = useTheme();
  //@ts-ignore
  const { localAppState } = React.useContext(AppContext);
  const { userData } = localAppState;

  useIsomorphicLayoutEffect(() => {
    if (treeData) {
      const treeList: NodeModel[] = [];
      treeData.forEach((element) => {
        const treeItem: NodeModel = {
          id: element.id,
          parent: element.parentID || 0,
          text: element.name,
          //@ts-ignore
          order: element.menuOrder,
          droppable: false,
          data: {
            type: element.type,
            route: element.route,
            basePath: element.basePath,
            icon: element.icon,
          },
        };
        treeList.push(treeItem);
      });

      setLocalTreeData([...treeList]);
      if (pathName) {
        const splitPath = pathName.split("/");
        const lastNumber = splitPath[splitPath.length - 1];
        const mainPath = "/" + splitPath[1] + "/" + splitPath[2];

        if (lastNumber === "") {
          setSelectedNode(
            //@ts-ignore
            treeList?.find((item) => item.data.route === "/" + splitPath[1])
          );
        } else {
          const findFullItem = treeList?.find(
            //@ts-ignore
            (item) => item.data.route === pathName
          );

          if (findFullItem) {
            setFoundItem(findFullItem);
          } else {
            const findFirstLevelItem = treeList?.find(
              //@ts-ignore
              (item) => item.data.route === mainPath
            );

            if (findFirstLevelItem) {
              setFoundItem(findFirstLevelItem);
            }
          }
        }
      }
    }
  }, [treeData, pathName]);

  const setFoundItem = (findItem) => {
    setSelectedNode(findItem);
    setInitialOpen([
      parseInt(findItem.parent.toString(), 10),
      parseInt(findItem.id.toString(), 10),
    ]);
  };

  const handleOnSelect = (node: NodeModel) => {
    //@ts-ignore
    switch (node.data.type) {
      case "link": {
        setSelectedNode(node);
        //@ts-ignore
        router.push(node.data.route);
        break;
      }
      case "logout": {
        handleLogoutClick();
      }
    }
  };

  const handleLogoutClick = () => {
    window.location.href = "/api/logout";
  };

  return (
    <Stack
      width={theme.dimensions.appDrawerWidth}
      minWidth={theme.dimensions.appDrawerWidth}
      height={"100%"}
      id="FlatDrawer"
      paddingLeft={4}
      paddingRight={4}
      paddingTop={8}
      sx={{
        backgroundColor: theme.appPalette.tertiary[98],
        // backgroundColor: "#FFFAF2"
      }}
    >
      <Stack
        id="FlatDrawerMainStack"
        height={"100%"}
        justifyContent={"space-between"}
      >
        <Stack
          height={"100%"}
          sx={{
            overflowY: "scroll",
            overflowX: "hidden",
            scrollbarWidth: "none",
            "&::MsOverflowStyle": "none",
            "&::-webkit-scrollbar": {
              width: 0,
              display: "none",
            },
          }}
        >
          <Stack mb={9} alignItems={"left"}>
            <Box
              component="img"
              sx={{
                height: 70,
                width: 180,
                paddingLeft: 5,
              }}
              //@ts-ignore
              alt="Cbex logo"
              src="https://storage.googleapis.com/cbex/cbex_groen_ondertitel_RGB.png"
            />
          </Stack>
          {localTreeData && localTreeData.length > 0 && (
            <DndProvider backend={MultiBackend} options={getBackendOptions()}>
              <Tree
                tree={localTreeData}
                rootId={0}
                ref={treeRef}
                initialOpen={initialOpen}
                enableAnimateExpand={true}
                insertDroppableFirst={false}
                render={(node, { onToggle, isOpen, ...rest }) => (
                  <CustomDrawerNode
                    //@ts-ignore
                    node={node}
                    isSelected={node.id === selectedNode?.id}
                    {...rest}
                    onToggle={(id) => {
                      if (currentID === node.id) {
                        setCurrentID(0);
                        treeRef.current?.closeAll();
                      } else {
                        treeRef.current?.closeAll();
                        setCurrentID(id);
                        setTimeout(() => {
                          treeRef.current?.open(id);
                        }, 5);
                      }
                    }}
                    onSelect={handleOnSelect}
                    isOpen={isOpen}
                  />
                )}
                dragPreviewRender={() => null}
                canDrag={() => false}
                canDrop={() => false}
                sort={false}
                onDrop={() => {}}
                classes={{
                  root: styles.treeRoot,
                  draggingSource: styles.draggingSource,
                  dropTarget: styles.dropTarget,
                  listItem: styles.listStyle,
                  placeholder: styles.placeholderContainer,
                  container: styles.container,
                }}
              />
            </DndProvider>
          )}
        </Stack>
        <Stack
          id="ProfileStack"
          height="80px"
          borderTop={"1px solid lightgray"}
        >
          <Box display="flex" flexDirection="row">
            {userData && (
              <Stack direction={"row"} alignItems={"center"}>
                <VscAccount />
                <Stack
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                  marginLeft={2}
                >
                  <CustomTypographyComponent
                    fontSize={14}
                    color={theme.palette.secondary.dark}
                  >
                    {userData.fullUserName}
                  </CustomTypographyComponent>
                  <CustomTypographyComponent
                    fontSize={12}
                    color={theme.palette.secondary.dark}
                  >
                    {userData.organization.name}
                  </CustomTypographyComponent>
                </Stack>
              </Stack>
            )}
          </Box>
        </Stack>
      </Stack>
    </Stack>
  );
};
